import { useState } from "react";
import { Link } from "react-router-dom";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumbs from "@/components/base/Breadcrumbs";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Serena | Private Alcohol Recovery Support | Serena's Quiet Recovery Space",
    "description": "Get in touch with Serena Raimes for private, non-judgemental alcohol recovery coaching. Book a free 30-minute conversation or send a confidential message. Support when you are ready.",
    "url": "https://serenasquietrecoveryspace.com/contact",
    "about": {
      "@type": "Organization",
      "name": "Serena's Quiet Recovery Space",
      "url": "https://serenasquietrecoveryspace.com",
      "email": "hello@serenarecovery.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "hello@serenarecovery.com",
        "contactType": "customer support",
        "availableLanguage": "English"
      }
    }
  };

  return (
    <main className="min-h-screen pt-20 md:pt-24">
      <SEOMeta
        title="Contact Serena | Private Recovery Support | Serena's Quiet Recovery Space"
        description="Get in touch with Serena Raimes for private alcohol recovery coaching. Book a free 30-minute conversation or send a confidential message. Support when you are ready — no obligation."
        canonicalUrl="/contact"
        ogType="website"
        schema={contactSchema}
      />
      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs
              items={[
                { label: "Home", href: "/" },
                { label: "Contact" },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-foreground-900 mb-4 text-center">
              Contact
            </h1>
            <p className="font-body text-sm md:text-base text-foreground-600 text-center max-w-2xl mx-auto mb-12 md:mb-16">
              Share a little about your situation and preferred support style. Serena will reply personally within two business days — <strong>no obligation</strong>.
            </p>

            <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start">
              <div className="w-full md:w-1/2">
                <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                  <h3 className="font-heading text-lg font-medium text-foreground-900 mb-4">
                    Book a Quiet Conversation
                  </h3>
                  <p className="font-body text-sm text-foreground-600 mb-5 leading-relaxed">
                    Choose a time that works for you. All conversations are <strong>confidential, calm, and without obligation</strong>.
                  </p>
                  <a
                    href="https://calendly.com/sraimes43/30min"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 bg-accent-500 text-background-50 font-body text-sm font-medium px-6 py-3 rounded-md hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-calendar-check-line text-base" />
                    Schedule a call on Calendly
                  </a>
                </div>

                <div className="bg-background-100 rounded-lg p-6 md:p-8 mb-8">
                  <h3 className="font-heading text-lg font-medium text-foreground-900 mb-4">
                    How to Reach Serena
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-primary-700">
                        <i className="ri-mail-line text-lg" />
                      </div>
                      <div>
                        <p className="font-body text-sm font-medium text-foreground-800">Email</p>
                        <p className="font-body text-sm text-foreground-600">hello@serenarecovery.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-primary-700">
                        <i className="ri-time-line text-lg" />
                      </div>
                      <div>
                        <p className="font-body text-sm font-medium text-foreground-800">Response Time</p>
                        <p className="font-body text-sm text-foreground-600">Within <strong>2 business days</strong></p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-primary-700">
                        <i className="ri-global-line text-lg" />
                      </div>
                      <div>
                        <p className="font-body text-sm font-medium text-foreground-800">Availability</p>
                        <p className="font-body text-sm text-foreground-600">Online, <strong>confidential, worldwide</strong></p>
                      </div>
                    </div>
                  </div>
                </div>

                {!submitted ? (
                  <form
                    id="contact-page-form"
                    action="https://readdy.ai/api/form/d8k253m0gbrp57rm5hb0"
                    method="POST"
                    data-readdy-form
                    className="space-y-5"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setFormError(false);
                      const form = e.currentTarget;
                      const formData = new FormData(form);

                      const honeypot = formData.get("website_alt") as string;
                      if (honeypot && honeypot.trim() !== "") {
                        setSubmitted(true);
                        form.reset();
                        return;
                      }
                      formData.delete("website_alt");

                      try {
                        const res = await fetch(form.action, {
                          method: "POST",
                          body: formData,
                          headers: {
                            Accept: "application/json",
                          },
                        });
                        if (res.ok) {
                          setSubmitted(true);
                          form.reset();
                        } else {
                          setFormError(true);
                        }
                      } catch {
                        setFormError(true);
                      }
                    }}
                  >
                    <div>
                      <label htmlFor="contact-name" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                        Name <span className="text-foreground-400">(required)</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                        Email <span className="text-foreground-400">(required)</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block font-body text-sm font-medium text-foreground-800 mb-2">
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-background-50 border border-background-300 rounded-md font-body text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300 resize-none"
                        placeholder="Tell me about your situation and what support you're looking for..."
                      />
                      <p className="mt-1 text-xs font-body text-foreground-400">Maximum 500 characters</p>
                    </div>
                    {formError && (
                      <div className="bg-accent-100 text-accent-900 font-body text-sm px-4 py-3 rounded-md">
                        Something went wrong. Please try again.
                      </div>
                    )}
                    <div className="contact-hp-field">
                      <label htmlFor="contact-website" className="block font-body text-xs text-foreground-400">
                        Website
                      </label>
                      <input
                        type="text"
                        id="contact-website"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden={true}
                        className="w-full px-3 py-2 bg-background-50 border border-background-300 rounded-md font-body text-xs text-foreground-400"
                        placeholder="https://"
                      />
                    </div>
                    <button
                      type="submit"
                      className="bg-foreground-900 text-background-50 font-body text-sm font-medium px-8 py-3 rounded-md hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap"
                    >
                      Submit
                    </button>
                  </form>
                ) : (
                  <div className="bg-primary-100 text-primary-900 font-body text-sm px-6 py-5 rounded-md">
                    <p className="font-medium mb-1">Thank you for reaching out.</p>
                    <p>Serena will reply personally within two business days. No obligation.</p>
                  </div>
                )}
              </div>

              <div className="w-full md:w-1/2">
                <div className="relative overflow-hidden rounded-lg">
                  <img
                    src="https://readdy.ai/api/search-image?query=Peaceful%20cozy%20interior%20corner%20with%20soft%20afternoon%20light%20through%20linen%20curtains%2C%20comfortable%20armchair%20with%20knitted%20throw%2C%20small%20side%20table%20with%20herbal%20tea%20and%20open%20journal%2C%20potted%20fern%2C%20warm%20neutral%20sage%20tones%2C%20editorial%20lifestyle%20photography%2C%20inviting%20calm%20atmosphere&width=800&height=1000&seq=contact-hero&orientation=portrait"
                    alt="Peaceful and inviting space for private recovery conversations"
                    title="Private Recovery Support Contact | Serena's Quiet Recovery Space"
                    width="800"
                    height="1000"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <style>{`
        .contact-hp-field {
          position: absolute;
          left: -9999px;
          opacity: 0;
          height: 0;
          overflow: hidden;
        }
      `}</style>
    </main>
  );
}